import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { getAuctionWins, getAuctionWinsFromYahoo } from '@/api/auction'
import IApiResponse from '@/types/api-response'
import IList from '@/types/list-result'
import IPagination from '@/types/pagination'

interface AuctionState {
	auction_wins: IAuctionWin[]
	auction_wins_pagination?: IPagination
	auction_wins_loading: boolean

	yahoo_auction_wins: IAuctionWinFromYahoo[]
	yahoo_auction_wins_loading: boolean
	yahoo_auction_wins_pagination?: IPagination

	error: string | null

	updating: boolean
}

export interface IAuctionWin {
	_id: string
	account_id: string
	account?: { username: string; display_name: string }
	code: string
	created_at: string
	error: string[]
	message: string
	name: string
	price: number
	proceed_params?: { [key: string]: any }
	time: string
	tracking_no: string | null
	updated_at: string
	url: string
}

export interface IAuctionWinFromYahoo {
	id: string
	message: string
	name: string
	price: number
	time: string
	url: string
}

const initialState: AuctionState = {
	auction_wins_loading: false,
	auction_wins: [],

	yahoo_auction_wins: [],
	yahoo_auction_wins_loading: false,

	error: null,

	updating: false,
}

export const fetchAuctionWins = createAsyncThunk<
	IApiResponse<IList<IAuctionWin>>,
	any
>('auction/fetch_auction_wins', async ({ params, cb }: any) => {
	try {
		const result = await getAuctionWins(params)
		if (typeof cb === 'function') {
			cb(result)
		}
		return result
	} catch (error: any) {
		const result = {
			success: false,
			message: error.message,
		}
		if (typeof cb === 'function') {
			cb(result)
		}
		return result
	}
})

export const fetchAuctionWinsFromYahoo = createAsyncThunk<
	IApiResponse<IList<IAuctionWinFromYahoo>>,
	any
>('auction/fetch_auction_wins_from_yahoo', async (params: any) => {
	return await getAuctionWinsFromYahoo(params)
})

const auctionSlice = createSlice({
	name: 'auction',
	initialState,
	reducers: {},
	extraReducers: (builder) => {
		builder
			.addCase(fetchAuctionWins.pending, (state) => {
				state.auction_wins_loading = true
				state.error = null
			})
			.addCase(fetchAuctionWins.fulfilled, (state, { payload }) => {
				state.auction_wins_loading = false
				if (payload.success) {
					state.auction_wins_pagination = payload.data!.pagination
					state.auction_wins = payload.data!.records
				} else {
					state.auction_wins = []
				}
			})
			.addCase(fetchAuctionWins.rejected, (state, action) => {
				state.auction_wins_loading = false
				state.error = action.error.message || 'Something went wrong'
			})

			.addCase(fetchAuctionWinsFromYahoo.pending, (state) => {
				state.yahoo_auction_wins_loading = true
				state.error = null
			})
			.addCase(
				fetchAuctionWinsFromYahoo.fulfilled,
				(state, { payload }) => {
					state.yahoo_auction_wins_loading = false
					if (payload.success) {
						state.yahoo_auction_wins_pagination = payload.data!.pagination
						state.yahoo_auction_wins = payload.data!.records
					} else {
						state.yahoo_auction_wins = []
					}
				}
			)
			.addCase(fetchAuctionWinsFromYahoo.rejected, (state, action) => {
				state.yahoo_auction_wins_loading = false
				state.error = action.error.message || 'Something went wrong'
			})
	},
})

export default auctionSlice.reducer
